import * as THREE from 'three';

// ============================================================
// ScreenShake.js - Camera shake system for game feel
// Supports multiple simultaneous shake sources with decay
// ============================================================

const _offset = new THREE.Vector3();

export class ScreenShake {
  constructor() {
    // Active shake instances
    this._shakes = [];
  }

  /**
   * Add a new shake.
   * @param {number} intensity - Max offset in world units (e.g. 0.05 for subtle, 0.15 for strong)
   * @param {number} duration - Duration in seconds
   * @param {number} [frequency=30] - Oscillations per second
   */
  add(intensity, duration, frequency = 30) {
    this._shakes.push({
      intensity,
      duration,
      frequency,
      elapsed: 0,
    });
  }

  /**
   * Convenience: melee hit shake (subtle, fast).
   */
  addMeleeHit() {
    this.add(0.04, 0.1, 35);
  }

  /**
   * Convenience: critical hit shake (stronger).
   */
  addCriticalHit() {
    this.add(0.07, 0.15, 40);
  }

  /**
   * Convenience: explosion shake (strong, slower).
   * @param {number} [scale=1] - Multiplier for intensity (0-1 based on distance)
   */
  addExplosion(scale = 1) {
    this.add(0.15 * scale, 0.3, 20);
  }

  /**
   * Update all active shakes and return the combined camera offset.
   * @param {number} dt - Delta time in seconds
   * @returns {THREE.Vector3} Offset to add to camera position
   */
  update(dt) {
    _offset.set(0, 0, 0);

    for (let i = this._shakes.length - 1; i >= 0; i--) {
      const s = this._shakes[i];
      s.elapsed += dt;

      if (s.elapsed >= s.duration) {
        this._shakes.splice(i, 1);
        continue;
      }

      // Decay: intensity drops linearly to 0
      const t = s.elapsed / s.duration;
      const decay = 1 - t;
      const amp = s.intensity * decay;

      // High-frequency oscillation (sine-based pseudo-random)
      const phase = s.elapsed * s.frequency * Math.PI * 2;
      _offset.x += Math.sin(phase * 1.0) * amp;
      _offset.y += Math.sin(phase * 1.3 + 1.7) * amp;
      _offset.z += Math.sin(phase * 0.9 + 3.1) * amp * 0.5;
    }

    return _offset;
  }

  /**
   * Check if any shake is active.
   * @returns {boolean}
   */
  isShaking() {
    return this._shakes.length > 0;
  }

  /**
   * Clear all active shakes.
   */
  clear() {
    this._shakes.length = 0;
  }
}
